import { useState } from 'react';
import { toast } from 'sonner';
import { Chip, DChip } from '@/components/kit';
import { errorText, useTechActions, type Device, type TechTask } from '../api';
import { TechModal } from './TechModal';

/** "Zeiss Cirrus · Maadi room 3 · online" */
export function deviceLine(d: Device): string {
  return [d.name, d.room, d.status === 'down' ? 'down' : 'online'].filter(Boolean).join(' · ');
}

/**
 * DEVICE · ROOM — the machine this test runs on, and the live toggle. Marking
 * it down changes every screen that names it; the confirm carries the toggle.
 * A clinic whose machine is not on file yet adds it here, where the technician
 * meets the gap — they are the one who knows the room.
 */
export function DeviceCard({ task }: { task: TechTask }) {
  const act = useTechActions();
  const [confirm, setConfirm] = useState(false);
  const [adding, setAdding] = useState(false);
  const d = task.device;
  const fail = (e: unknown) => toast.error(errorText(e));
  return (
    <div className="card">
      <p className="t-ovl mut">Device · room</p>
      {d ? (
        <div>
          <p className={`t-sec mt8${d.status === 'down' ? ' red' : ''}`}>{deviceLine(d)}</p>
          <p className="mt8">
            {d.status === 'down' ? (
              <DChip kind="outline" onClick={() => act.deviceUp.mutate(d.id, {
                onSuccess: () => toast.success(`${d.name} is back online`),
                onError: fail,
              })}>
                Mark it back up
              </DChip>
            ) : (
              <DChip kind="warn" onClick={() => setConfirm(true)}>Mark device down</DChip>
            )}
          </p>
        </div>
      ) : (
        <div>
          <p className="t-sec mut mt8">No machine on file for {task.kindLabel}</p>
          <p className="mt8"><DChip kind="outline" onClick={() => setAdding(true)}>Add the machine</DChip></p>
        </div>
      )}
      {d ? (
        <MarkDownModal open={confirm} device={d} kindLabel={task.kindLabel} onClose={() => setConfirm(false)} />
      ) : null}
      <AddDeviceModal open={adding} kind={task.kind} kindLabel={task.kindLabel} onClose={() => setAdding(false)} />
    </div>
  );
}

export function MarkDownModal({ open, device, kindLabel, onClose }: {
  open: boolean;
  device: Device;
  kindLabel: string;
  onClose: () => void;
}) {
  const act = useTechActions();
  const [note, setNote] = useState('');
  return (
    <TechModal
      open={open}
      onClose={onClose}
      title={`Mark ${device.name} down?`}
      sub={`Nothing can be captured on it. Every pending and future ${kindLabel} re-routes until it is back, and the desk is told.`}
      foot={(
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn"
            style={{ background: 'var(--warning-600)', color: 'var(--surface-0)' }}
            disabled={act.deviceDown.isPending}
            onClick={() => act.deviceDown.mutate({ id: device.id, note }, {
              onSuccess: () => {
                toast.success(`${device.name} is marked down`);
                onClose();
              },
              onError: (e) => toast.error(errorText(e)),
            })}
          >
            Mark down
          </button>
        </>
      )}
    >
      <label className="t-ovl mut" style={{ display: 'block', marginTop: 16 }} htmlFor="tech-down-note">
        What is wrong <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
      </label>
      <input id="tech-down-note" className="tech-inp" value={note} maxLength={300}
        placeholder="e.g. scan head error on start-up" onChange={(e) => setNote(e.target.value)} />
    </TechModal>
  );
}

const KIND_CHOICES: Array<{ kind: string; label: string }> = [
  { kind: 'oct', label: 'OCT' },
  { kind: 'refraction', label: 'Refraction' },
  { kind: 'visual_field', label: 'Visual field' },
  { kind: 'fundus_photo', label: 'Fundus photo' },
  { kind: 'topography', label: 'Topography' },
  { kind: 'biometry', label: 'Biometry' },
  { kind: 'iop', label: 'IOP' },
  { kind: 'xray', label: 'X-ray' },
  { kind: 'ultrasound', label: 'Ultrasound' },
  { kind: 'echo', label: 'Echo' },
  { kind: 'ecg', label: 'ECG' },
  { kind: 'dexa', label: 'DEXA' },
  { kind: 'mammography', label: 'Mammography' },
  { kind: 'spirometry', label: 'Spirometry' },
  { kind: 'audiometry', label: 'Audiometry' },
];

export function AddDeviceModal({ open, kind, kindLabel, onClose }: {
  open: boolean;
  kind: string;
  kindLabel: string;
  onClose: () => void;
}) {
  const act = useTechActions();
  const [name, setName] = useState('');
  const [room, setRoom] = useState('');
  const [kinds, setKinds] = useState<string[]>(kind && kind !== 'other' ? [kind] : []);
  const toggle = (k: string) => setKinds((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));
  const ok = name.trim().length > 0 && kinds.length > 0;
  return (
    <TechModal
      open={open}
      onClose={onClose}
      title="Add the machine"
      sub={`The machine ${kindLabel} runs on — its room, and the tests it does`}
      width={520}
      foot={(
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ok || act.addDevice.isPending}
            onClick={() => act.addDevice.mutate({ name: name.trim(), room: room.trim() || undefined, kinds }, {
              onSuccess: () => {
                toast.success(`${name.trim()} added`);
                onClose();
              },
              onError: (e) => toast.error(errorText(e)),
            })}
          >
            Add
          </button>
        </>
      )}
    >
      <label className="t-ovl mut" style={{ display: 'block', marginTop: 16 }} htmlFor="tech-dev-name">Machine</label>
      <input id="tech-dev-name" className="tech-inp" value={name} autoFocus maxLength={120}
        placeholder="e.g. Zeiss Cirrus" onChange={(e) => setName(e.target.value)} />
      <label className="t-ovl mut" style={{ display: 'block', marginTop: 12 }} htmlFor="tech-dev-room">Room</label>
      <input id="tech-dev-room" className="tech-inp" value={room} maxLength={60}
        placeholder="e.g. Room 3" onChange={(e) => setRoom(e.target.value)} />
      <p className="t-ovl mut" style={{ marginTop: 12 }}>Tests it does</p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
        {KIND_CHOICES.map((k) => (
          <Chip key={k.kind} small kind={kinds.includes(k.kind) ? 'brand' : 'outline'}
            className={kinds.includes(k.kind) ? 'w6' : undefined} onClick={() => toggle(k.kind)}>
            {k.label}
          </Chip>
        ))}
      </div>
    </TechModal>
  );
}
